// Mutations for slot docs + the JSONB sub-lists (photos, empotage files,
// redevances). Draft BL has its own hook because it triggers state
// changes (clears okPourImpression / draftBLWeightAck).

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  ackDraftBL,
  createTraceabilityShare,
  deleteEmpotageFile,
  deletePhoto,
  deleteRedevance,
  deleteSlotDoc,
  markDefinitiveBL,
  registerDraftBL,
  registerEmpotageFile,
  registerPhoto,
  registerRedevance,
  resendTraceabilityShare,
  revokeTraceabilityShare,
  unmarkDefinitiveBL,
  upsertSlotDoc,
} from "@/features/transit/api/transit.api"
import type {
  AckDraftBLPayload,
  MarkDefinitiveBLPayload,
  RegisterDraftBLPayload,
  RegisterEmpotageFilePayload,
  RegisterPhotoPayload,
  RegisterRedevancePayload,
  RegisterSlotDocPayload,
  ShareTraceabilityPayload,
} from "@/features/transit/api/types"
import { transitKeys } from "@/features/transit/hooks/use-transit-orders"
import { resolveApiError } from "@/lib/api/error-i18n"

// Force a synchronous refresh of the active OT detail. We don't trust
// the mutation response payload — backend may return a partial embed
// shape that strips lots/docs/blockers, which would clobber the cache.
function useInvalidate(otId: number | string) {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.refetchQueries({
      queryKey: transitKeys.detail(otId),
      type: "active",
    })
    await queryClient.invalidateQueries({ queryKey: transitKeys.all })
  }
}

// Pulls the translator once per hook and produces a uniform error
// handler so every onError site reads the same.
function useToastError() {
  const { t } = useTranslation("transit")
  return (error: unknown) => toast.error(resolveApiError(error, t))
}

// ── Slot docs (16 stable ids) ──────────────────────────────────────

export function useUpsertSlotDoc(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      slotId,
      payload,
    }: {
      slotId: string
      payload: RegisterSlotDocPayload
    }) => upsertSlotDoc(otId, slotId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeleteSlotDoc(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (slotId: string) => deleteSlotDoc(otId, slotId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

// ── Photos / empotage files / redevances ───────────────────────────

export function useRegisterPhoto(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: RegisterPhotoPayload) => registerPhoto(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeletePhoto(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (photoId: string) => deletePhoto(otId, photoId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useRegisterEmpotageFile(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: RegisterEmpotageFilePayload) =>
      registerEmpotageFile(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeleteEmpotageFile(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (fileId: string) => deleteEmpotageFile(otId, fileId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useRegisterRedevance(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: RegisterRedevancePayload) =>
      registerRedevance(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useDeleteRedevance(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (redevanceId: string) => deleteRedevance(otId, redevanceId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

// ── Embarquement: draft BL + ack + OK pour impression ──────────────

export function useRegisterDraftBL(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: RegisterDraftBLPayload) =>
      registerDraftBL(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useAckDraftBL(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: AckDraftBLPayload) => ackDraftBL(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useMarkDefinitiveBL(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: ({
      draftId,
      payload,
    }: {
      draftId: number | string
      payload: MarkDefinitiveBLPayload
    }) => markDefinitiveBL(otId, draftId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useUnmarkDefinitiveBL(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: () => unmarkDefinitiveBL(otId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

// ── Terminer: traceability share ───────────────────────────────────

export function useCreateTraceabilityShare(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (payload: ShareTraceabilityPayload) =>
      createTraceabilityShare(otId, payload),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useRevokeTraceabilityShare(otId: number | string) {
  const invalidate = useInvalidate(otId)
  const toastError = useToastError()
  return useMutation({
    mutationFn: (shareId: number | string) =>
      revokeTraceabilityShare(otId, shareId),
    onSuccess: () => invalidate(),
    onError: toastError,
  })
}

export function useResendTraceabilityShare(otId: number | string) {
  const toastError = useToastError()
  return useMutation({
    mutationFn: (shareId: number | string) =>
      resendTraceabilityShare(otId, shareId),
    onError: toastError,
  })
}
